// Fetches ASAP services status
import { normalizeStatus } from '../utils/statusUtils';

const TIMEOUT_MS = 10000; // 10 second timeout

/**
 * Fetches a health endpoint with timeout and error handling
 */
async function fetchWithTimeout(url, options = {}) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      mode: 'cors', // Explicitly request CORS
      headers: {
        'Accept': 'application/json',
        ...options.headers
      }
    });
    clearTimeout(timeoutId);
    return { response, error: null };
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      return { response: null, error: new Error('Request timeout') };
    }
    // Check if it's a CORS error
    if (error.message && (
      error.message.includes('CORS') || 
      error.message.includes('cross-origin') ||
      error.message.includes('Failed to fetch') ||
      error.name === 'TypeError'
    )) {
      return { response: null, error: new Error('CORS blocked: Server does not allow cross-origin requests') };
    }
    return { response: null, error };
  }
}

/**
 * Fetches health status for a single service
 */
async function fetchServiceHealth(url, serviceName) {
  const { response, error } = await fetchWithTimeout(url);
  const lastChecked = new Date();

  if (error || !response) {
    // Check if it's a CORS error specifically
    const isCorsError = error?.message?.includes('CORS');
    return {
      name: serviceName,
      status: isCorsError ? 'Unknown' : 'Major Outage',
      lastChecked,
      error: error?.message || 'Network error',
      raw: null,
      corsBlocked: isCorsError
    };
  }

  if (!response.ok) {
    return {
      name: serviceName,
      status: response.status >= 500 ? 'Major Outage' : 'Degraded Performance',
      lastChecked,
      httpStatus: response.status,
      raw: null
    };
  }

  try {
    const json = await response.json();
    // Handle different response formats
    let status = 'Unknown';
    
    if (json.status !== undefined) {
      status = json.status === 200 || json.status === 'healthy' ? 'Operational' : String(json.status);
    } else if (json.health !== undefined) {
      status = json.health === 'healthy' ? 'Operational' : json.health;
    } else if (json.running !== undefined) {
      status = json.running === true ? 'Operational' : 'Major Outage';
    }

    return {
      name: serviceName,
      status: normalizeStatus(status),
      lastChecked,
      raw: json
    };
  } catch (parseError) {
    // If response is OK but not JSON, consider it operational
    return {
      name: serviceName,
      status: 'Operational',
      lastChecked,
      raw: { note: 'Non-JSON response' }
    };
  }
}

/**
 * Fetches ASAP services status
 * 
 * TODO: Replace the placeholder URLs below with your actual ASAP service endpoints
 * Example endpoints might be:
 * - API endpoints: https://asap-api.azurewebsites.net/health
 * - Service endpoints: https://asap-service.azurewebsites.net/health
 * - Database/backend: https://asap-backend.azurewebsites.net/health
 */
export async function fetchAsapServicesStatus() {
  const endpoints = [
    { url: 'https://go.asapconnected.com/', name: 'GO Site' },
    { url: 'https://admin.asapconnectedstage.com/account/login?ReturnUrl=%2f', name: 'Admin 4' },
    // Add more ASAP service endpoints as needed
    // { url: 'https://asap-api.azurewebsites.net/health', name: 'ASAP API' },
    // { url: 'https://asap-service.azurewebsites.net/health', name: 'ASAP Service' },
  ];

  try {
    const results = await Promise.allSettled(
      endpoints.map(endpoint => fetchServiceHealth(endpoint.url, endpoint.name))
    );

    return results.map((result, index) => {
      if (result.status === 'fulfilled') {
        return result.value;
      }
      return {
        name: endpoints[index].name,
        status: 'Major Outage',
        lastChecked: new Date(),
        error: result.reason?.message || 'Unknown error',
        raw: null
      };
    });
  } catch (error) {
    // Fallback if Promise.allSettled fails (shouldn't happen, but just in case)
    return endpoints.map(endpoint => ({
      name: endpoint.name,
      status: 'Major Outage',
      lastChecked: new Date(),
      error: error.message || 'Failed to fetch status',
      raw: null
    }));
  }
}

